// ============================================================
// FACING - which arc a shot strikes.
//
// A vehicle's Front, Side and Rear armour are chosen by WHERE THE SHOT
// COMES FROM relative to the hull. That is geometry: flanking a Rhino is
// a tactical achievement precisely because rear armour is weaker. Until
// v8.50.0 it was a radio button and the map had no say in it.
//
// IT IS A RELATION, NOT A PROPERTY. Two attackers on opposite sides of a
// tank strike different facings at the same moment, which is why nothing
// here is ever stored on the defender - the same lesson cover learned in
// v7.51.0, when a single shared `state.cover` meant the second
// derivation silently replaced the first.
//
// ANGLES ARE DEGREES, 0 = DUE EAST, matching `Math.atan2(z, x)`. That is
// the convention that survived the deletion of token facing in v8.29.0
// (`deployment.ts:43`) and it is world space throughout. Nothing in this
// file knows about the camera, the screen, or which way is up.
//
// Pure. Zero React dependency.
// ============================================================
import type { VehicleFacing } from '../vehicleFacing';

/**
 * Which armour value a hit uses.
 *
 * Re-exported from `engine/vehicleFacing`, which owns the RULE. This
 * module owns only the geometry that answers it, so the combat engine
 * can read the rule without importing the map.
 */
export type FacingArc = VehicleFacing;

/** How the circle is carved up. A table's ruling, not a fact. */
export type ArcPreset = 'quarters' | 'narrow';

/**
 * HALF-WIDTHS, in degrees, measured either side of the bow and the stern.
 * The sides get whatever is left, so the four always close the circle.
 *
 * `quarters` is the common tabletop convention and the default.
 * `narrow` makes a clean flank harder and a true rear shot an
 * achievement - 60° fore and aft, 120° down each side.
 */
export const ARC_PRESETS: Record<ArcPreset, { front: number; rear: number }> = {
  quarters: { front: 45, rear: 45 },
  narrow: { front: 30, rear: 30 },
};

/**
 * The same two, as a table a GM can read.
 *
 * Which armour a shot meets is decided by where it comes from, and
 * tables disagree about how generous the bow and stern should be - so it
 * is a ruling, not a fact. Shaped like `COVER_CONFLICT_MODES`, and
 * consumed the same way by the permissions modal.
 */
export const VEHICLE_ARC_PRESETS: { key: ArcPreset; label: string; hint: string }[] = [
  {
    key: 'quarters',
    label: '90° all round (front, both sides, rear)',
    hint: 'The common tabletop convention: the circle in four equal quarters. A shot 40° off the bow is a frontal.',
  },
  {
    key: 'narrow',
    label: '60° front and rear, 120° each side',
    hint: 'A narrow bow and stern, wide flanks. A clean flank is easier to earn and a true rear shot is an achievement - that same 40° shot is a side hit.',
  },
];

const DEG = 180 / Math.PI;

/**
 * Enough of a point to take a bearing from.
 *
 * `y` is accepted and IGNORED. Callers hand over whole tokens and Vec3s,
 * and an arc is a question about the ground plan - saying so in the type
 * is cheaper than making every caller strip a field.
 */
export interface GroundPoint {
  x: number;
  z: number;
  y?: number;
}

/** A real number: a heading or a bearing that can be reasoned with (null, undefined and NaN cannot). */
export const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** Fold any angle into [0, 360). */
export function wrap360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Fold any angle into (-180, 180] - the signed offset from a heading. */
export function wrap180(deg: number): number {
  const a = wrap360(deg);
  return a > 180 ? a - 360 : a;
}

/**
 * The bearing from `from` to `to`, in degrees, 0 = due east.
 *
 * A PLAN-VIEW ANSWER. Height is discarded on purpose: an arc is a
 * question about which side of the hull a shot arrives on, and a
 * gunship directly overhead is still coming from wherever it is standing
 * on the map. `groundDistance` in `measure.ts` drops height for the same
 * reason.
 */
export function bearingBetween(from: GroundPoint, to: GroundPoint): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  // Shooting from exactly where you stand has no direction. Zero rather
  // than NaN, so a degenerate case reads as "dead ahead" instead of
  // poisoning every comparison downstream.
  if (dx === 0 && dz === 0) return 0;
  return wrap360(Math.atan2(dz, dx) * DEG);
}

/**
 * Which arc a shot arriving on `shooterBearing` strikes on a hull
 * pointing along `heading`.
 *
 * `shooterBearing` is measured FROM THE TARGET TO THE SHOOTER - which
 * side the shot arrives on, not which way it travels. Getting that
 * backwards swaps front and rear and looks entirely plausible.
 *
 * Returns `null` when there is nothing to say: a hull with no heading
 * has never been turned, and the caller should leave the facing alone
 * rather than invent one.
 */
export function arcFor(
  heading: number | null | undefined,
  shooterBearing: number | null | undefined,
  preset: ArcPreset,
): FacingArc | null {
  if (!finite(heading) || !finite(shooterBearing)) return null;
  // Validated rather than trusted: the permissions document outlives the
  // code that wrote it, and a later build may name a preset this one has
  // never heard of.
  const widths = ARC_PRESETS[preset] ?? ARC_PRESETS.quarters;
  const off = Math.abs(wrap180(shooterBearing - heading));
  // INCLUSIVE AT BOTH EDGES, so a shot exactly on a boundary does not
  // depend on floating-point rounding to decide which armour it meets.
  if (off <= widths.front) return 'front';
  if (off >= 180 - widths.rear) return 'rear';
  return 'side';
}

/**
 * A point on the ground `radius` metres from `centre`, along `heading`.
 *
 * This is how the heading arrow is placed: project THIS world point and
 * take the screen direction from the footprint. The alternative -
 * parameterising the footprint's screen ellipse by the same angle - is
 * wrong at every camera but straight down, and wrong in a way that looks
 * plausible. `project` already knows how the ground plane maps to the
 * screen at any yaw and pitch; nothing here needs to.
 */
export function headingPoint(centre: GroundPoint, heading: number, radius: number): { x: number; z: number } {
  const rad = heading / DEG;
  return { x: centre.x + Math.cos(rad) * radius, z: centre.z + Math.sin(rad) * radius };
}
